/**
 * Interactive RT60 Acoustic Reverberation Room Simulator
 * Based on Sabine's Formula: RT60 = (0.161 * Volume) / (Total Absorption Area)
 */
export function initAcousticSimulator() {
  const roomTypeSelect = document.getElementById('roomTypeSelect');
  const lengthSlider = document.getElementById('roomLength');
  const widthSlider = document.getElementById('roomWidth');
  const heightSlider = document.getElementById('roomHeight');
  const treatmentSelect = document.getElementById('treatmentLevel');

  const lengthVal = document.getElementById('lengthVal');
  const widthVal = document.getElementById('widthVal');
  const heightVal = document.getElementById('heightVal');
  
  const rt60GaugeNum = document.getElementById('rt60GaugeNum');
  const rt60Circle = document.getElementById('rt60Circle');
  const simResultBadge = document.getElementById('simResultBadge');
  const simAdviceText = document.getElementById('simAdviceText');

  if (!roomTypeSelect || !lengthSlider) return;

  const roomTargets = {
    studio: { name: "Recording Studio Control Room", targetMin: 0.3, targetMax: 0.5, desc: "Acoustically dead for accurate sound monitoring." },
    cinema: { name: "Surround Sound Cinema", targetMin: 0.5, targetMax: 0.8, desc: "Controlled reflection for immersive Dolby surround audio." },
    auditorium: { name: "Multi-purpose Auditorium", targetMin: 1.0, targetMax: 1.5, desc: "Resonant, vibrant acoustics suited for live speech and orchestra." },
    classroom: { name: "Hi-Tech Smart Classroom", targetMin: 0.5, targetMax: 0.7, desc: "Clear speech intelligibility for telepresence microphones." }
  };

  const absorptionCoeffs = {
    light: 0.12,    // Untreated drywall / concrete
    moderate: 0.35, // Acoustic ceiling panels + curtains
    full: 0.65      // High-density Bangs & Bangs acoustic diffusers & absorbers
  };

  function calculateRT60() {
    const l = parseFloat(lengthSlider.value);
    const w = parseFloat(widthSlider.value);
    const h = parseFloat(heightSlider.value);

    lengthVal.textContent = `${l} m`;
    widthVal.textContent = `${w} m`;
    heightVal.textContent = `${h} m`;

    const volume = l * w * h; // Volume in m³
    const surfaceArea = 2 * (l * w + l * h + w * h); // Total surface area in m²
    
    const alpha = absorptionCoeffs[treatmentSelect.value] || 0.35;
    const totalAbsorption = surfaceArea * alpha; // Sabins

    // RT60 Calculation in seconds
    let rt60 = (0.161 * volume) / totalAbsorption;
    rt60 = Math.round(rt60 * 100) / 100; // Round to 2 decimal places

    // Update gauge text
    rt60GaugeNum.textContent = rt60;

    // SVG Circle stroke dash offset (Gauge visualization)
    const maxVal = 2.5;
    const circumference = 2 * Math.PI * 70; // r = 70
    const progress = Math.min(rt60 / maxVal, 1);
    const strokeDashoffset = circumference - (progress * circumference);
    rt60Circle.style.strokeDasharray = `${circumference}`;
    rt60Circle.style.strokeDashoffset = strokeDashoffset;

    // Evaluate against target
    const currentTarget = roomTargets[roomTypeSelect.value] || roomTargets.studio;
    if (rt60 >= currentTarget.targetMin && rt60 <= currentTarget.targetMax) {
      simResultBadge.textContent = "Optimal Acoustic Balance";
      simResultBadge.className = "sim-result-badge badge-optimal";
      simAdviceText.textContent = `RT60 is perfectly optimized for ${currentTarget.name}. ${currentTarget.desc}`;
    } else if (rt60 > currentTarget.targetMax) {
      simResultBadge.textContent = "Excessive Reverberation (Echoey)";
      simResultBadge.className = "sim-result-badge";
      simResultBadge.style.background = "rgba(239, 68, 68, 0.15)";
      simResultBadge.style.color = "#ef4444";
      simResultBadge.style.border = "1px solid rgba(239, 68, 68, 0.4)";
      simAdviceText.textContent = `Room requires high-absorption acoustic wall panels to bring RT60 down from ${rt60}s to the target range (${currentTarget.targetMin}s - ${currentTarget.targetMax}s).`;
    } else {
      simResultBadge.textContent = "Over-Dampened (Sound Dead)";
      simResultBadge.className = "sim-result-badge";
      simResultBadge.style.background = "rgba(245, 158, 11, 0.15)";
      simResultBadge.style.color = "#f59e0b";
      simResultBadge.style.border = "1px solid rgba(245, 158, 11, 0.4)";
      simAdviceText.textContent = `Space is acoustic-heavy. Incorporate acoustic diffusers to restore natural warmth and reverberation.`;
    }
  }

  [roomTypeSelect, lengthSlider, widthSlider, heightSlider, treatmentSelect].forEach(input => {
    input.addEventListener('input', calculateRT60);
    input.addEventListener('change', calculateRT60);
  });

  calculateRT60();
}
